import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { NEAR_USER } from 'src/constants'
import { useFilterProvider } from 'src/context/FilterProvider'
import useUserLocation from 'src/hooks/useUserLocation'
import { toSearchInputValue } from 'src/utils/locationLabel'
import ga from 'src/utils/pixels/gtag'
import {
  SIZE_TYPE_MAP,
  SPACE_TYPE_LOCATION_TYPES,
  SPACE_TYPE_MAP
} from './SpaceTypeFilters'
import { SearchFilters, SpaceType } from './types'

interface UseSearchBarProps {
  onSearch?: (filters: SearchFilters) => void
}

export const useSearchBar = ({ onSearch }: UseSearchBarProps) => {
  const { t } = useTranslation('common')
  const { filters, updateParams, isReady } = useFilterProvider()
  const { getUserLocation } = useUserLocation()

  // `search` is what the renter READS in the "DÓNDE" input, so it goes
  // through toSearchInputValue — on /search these params often hold the
  // canonical city slug (a SearchPageCity value) written back by the map/
  // pagination `...mode` spread, which rendered as "san-pedro-garza-garcia".
  // `description` stays raw: it is what gets pushed back into the URL, and
  // the slug form is what the analytics + mode plumbing expects.
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    search: toSearchInputValue(filters.search ?? filters.description),
    placeId: filters.placeId ?? undefined,
    description: filters.description ?? filters.search ?? undefined,
    spaceTypes: filters.spaceTypes ?? []
  })

  useEffect(() => {
    if (isReady) {
      let spaceTypes: SpaceType[] = []
      if (
        filters.sizes &&
        (!filters.spaceTypes || filters.spaceTypes.length === 0)
      ) {
        spaceTypes = filters.sizes.map((size) => {
          const spaceType = SIZE_TYPE_MAP[size]
          return spaceType
        })
        spaceTypes = [...new Set(spaceTypes)]
      }
      setSearchFilters({
        search: toSearchInputValue(filters.search ?? filters.description),
        placeId: filters.placeId ?? undefined,
        description: filters.description ?? filters.search ?? undefined,
        spaceTypes: filters.spaceTypes ?? spaceTypes
      })
    }
  }, [isReady, filters])

  // Denominator for the nav-search funnel (search_box_start →
  // search_suggestion_select → search → search_completed). Fires once per
  // mount on the FIRST engagement with the search box — first keystroke
  // (updateSearch) or first location select — mirroring the
  // capture_lead_start first-focus pattern. Without it, "typed but never
  // submitted" abandonment is invisible: only the submit (`search`) fires.
  const searchBoxStartedRef = useRef(false)
  const trackSearchBoxStart = () => {
    if (searchBoxStartedRef.current) return
    searchBoxStartedRef.current = true
    ga.event('search_box_start')
  }

  const handleLocationSelect = (args: {
    search?: string
    placeId?: string
    description?: string
  }) => {
    trackSearchBoxStart()
    setSearchFilters({
      ...searchFilters,
      search: args.search ?? '',
      placeId: args.placeId,
      description: args.description
    })
  }

  const handleFilterSelect = (spaceType: SpaceType) => {
    setSearchFilters((prev) => {
      // If selecting ALL, clear all other selections
      if (spaceType === SpaceType.ALL) {
        return {
          ...prev,
          spaceTypes: [SpaceType.ALL]
        }
      }

      if (spaceType === SpaceType.LAND) {
        return {
          ...prev,
          spaceTypes: [SpaceType.LAND]
        }
      }

      // If ALL is currently selected and user selects something else, remove ALL
      if (prev.spaceTypes.includes(SpaceType.ALL)) {
        return {
          ...prev,
          spaceTypes: [spaceType]
        }
      }

      if (prev.spaceTypes.includes(SpaceType.LAND)) {
        return {
          ...prev,
          spaceTypes: [spaceType]
        }
      }

      // If deselecting the current selection, allow it
      if (prev.spaceTypes.includes(spaceType)) {
        return {
          ...prev,
          spaceTypes: prev.spaceTypes.filter((id) => id !== spaceType)
        }
      }

      // Add new selection
      return {
        ...prev,
        spaceTypes: [...prev.spaceTypes, spaceType]
      }
    })
  }

  const handleResetTypes = () => {
    setSearchFilters({ ...searchFilters, spaceTypes: [] })
  }

  const handleClearAll = () => {
    setSearchFilters({
      search: '',
      placeId: undefined,
      description: undefined,
      spaceTypes: []
    })
  }

  const handleShowSpaces = async () => {
    const { description, placeId, spaceTypes } = searchFilters ?? {}
    const coords = placeId === NEAR_USER ? await getUserLocation() : undefined
    ga.event('search', searchFilters)
    const sizes = [
      ...new Set(
        spaceTypes
          .map((type) => SPACE_TYPE_MAP[type].query.sizes)
          .flat()
          .filter((size) => size !== undefined)
      )
    ]
    // Union of underlying LocationType enum values for every selected
    // SpaceType. Sending these keeps mini-bodegas out of PARKING+NAVE
    // searches where the area-based `sizes` filter would otherwise admit
    // them. See SPACE_TYPE_LOCATION_TYPES for the grouping.
    const locationTypes = [
      ...new Set(
        spaceTypes.flatMap((type) => SPACE_TYPE_LOCATION_TYPES[type] ?? [])
      )
    ]
    const canStoreVehicle = spaceTypes.map(
      (type) => SPACE_TYPE_MAP[type].query.canStoreVehicle
    )
    const either =
      (canStoreVehicle.some((value) => value === true) &&
        canStoreVehicle.some((value) => value === false)) ||
      canStoreVehicle.every((value) => value === undefined)

    const canStoreVehicleQuery = either
      ? undefined
      : spaceTypes.reduce(
          (acc, type) =>
            acc || (SPACE_TYPE_MAP[type].query.canStoreVehicle ?? false),
          false
        )

    // Only override map coordinates when we have a new location source.
    // When no place is selected (e.g. user only changed space type), preserve
    // the current lat/lng/zoom so the map area stays in sync with the query.
    //
    // `city`/`hood` are cleared alongside the coords they belong to. They
    // describe the OLD place, and `updateParams` merges
    // `{...filters, ...initialFilters, ...params}` before pushing to /search —
    // so anything not named here survives the location change. That is how a
    // Monterrey pillar page's `city=monterrey` (read off the `/[pillar]/[city]`
    // route segment) rode along into a Cancún search and made every downstream
    // consumer of `filters.city` describe the wrong city: the on-demand card
    // headline, and the `city` property on the `search_completed` PostHog
    // event that docs/marketing/liquidity.md slices on.
    //
    // The no-new-place branch deliberately keeps them: changing only the space
    // type must not discard the city the renter is browsing.
    const locationParams: Partial<
      Pick<
        Parameters<typeof updateParams>[0],
        'lat' | 'lng' | 'zoom' | 'city' | 'hood'
      >
    > = coords
      ? {
          lat: coords.lat,
          lng: coords.lng,
          zoom: undefined,
          city: undefined,
          hood: undefined
        }
      : placeId
        ? {
            lat: undefined,
            lng: undefined,
            zoom: undefined,
            city: undefined,
            hood: undefined
          }
        : {}

    updateParams({
      search: description,
      placeId,
      description,
      spaceTypes,
      ...locationParams,
      page: undefined,
      sizes,
      locationTypes: locationTypes.length > 0 ? locationTypes : undefined,
      canStoreVehicle: canStoreVehicleQuery
    })

    onSearch?.(searchFilters)
  }

  const getDisplayText = (): string => {
    const { spaceTypes } = searchFilters
    if (spaceTypes.length === 0) {
      return t('SEARCH_BAR.ALL_TYPES')
    } else if (spaceTypes.length === 1) {
      const label = SPACE_TYPE_MAP[spaceTypes[0]].label
      return t(label)
    } else {
      return t('SEARCH_BAR.TYPES', { count: spaceTypes.length })
    }
  }

  const updateSearch = (search: string) => {
    trackSearchBoxStart()
    setSearchFilters({
      ...searchFilters,
      search,
      placeId: undefined
    })
  }

  return {
    searchFilters,
    handleLocationSelect,
    handleFilterSelect,
    handleResetTypes,
    handleClearAll,
    handleShowSpaces,
    getDisplayText,
    updateSearch
  }
}
